/**
 * SeoLandingPage.tsx  -  Renders a public SEO landing page from seoPages.ts.
 *
 * Mounted at /:seoSlug. Sets document title, meta description, canonical and
 * FAQPage JSON-LD on mount so each page is independently indexable.
 */

import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { getSeoPage } from "@/data/seoPages";
import SiteFooter from "@/components/SiteFooter";
import LitlingsMark from "@/components/LitlingsMark";

const SITE = "https://litlings.com";

/**
 * Set a <meta> tag's content, creating it if absent.
 * Returns a cleanup that restores the previous value (or removes the tag).
 */
function setMeta(
  keyAttr: "name" | "property",
  keyValue: string,
  content: string,
): () => void {
  const selector = `meta[${keyAttr}="${keyValue}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(keyAttr, keyValue);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  const node = el;
  return () => {
    if (created) node.remove();
    else if (prev !== null) node.setAttribute("content", prev);
  };
}

export default function SeoLandingPage({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const page = getSeoPage(slug);

  useEffect(() => {
    if (!page) return;
    const prevTitle = document.title;
    document.title = page.title;

    const cleanups = [
      setMeta("name", "description", page.description),
      setMeta("property", "og:title", page.title),
      setMeta("property", "og:description", page.description),
      setMeta("property", "og:url", `${SITE}/${page.slug}`),
    ];

    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const prevCanonical = canonical?.getAttribute("href") ?? null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE}/${page.slug}`);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (prevCanonical) canonical?.setAttribute("href", prevCanonical);
      ld.remove();
    };
  }, [page]);

  if (!page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-3xl font-extrabold">Page not found</h1>
        <p className="text-portal-muted">
          That page does not exist. Try the home page.
        </p>
        <Link to="/" className="font-bold text-success hover:underline">
          Back to Litlings
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-portal-border">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <LitlingsMark size={32} />
            <span className="font-display text-lg font-extrabold">Litlings</span>
          </Link>
          <button
            onClick={() => navigate("/signup")}
            className="btn-primary"
          >
            Get started free
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14">
        <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
          {page.h1}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-portal-text-soft">
          {page.intro}
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/signup")}
            className="btn-primary px-6 py-3 text-base"
          >
            {page.ctaLabel} <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/"
            className="btn-secondary px-6 py-3 text-base"
          >
            See everything Litlings does
          </Link>
        </div>

        {page.sections.map((s) => (
          <section key={s.heading} className="mt-12">
            <h2 className="font-display text-2xl font-bold">{s.heading}</h2>
            <p className="mt-3 leading-relaxed text-portal-text-soft">{s.body}</p>
          </section>
        ))}

        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold">Common questions</h2>
          <dl className="mt-5 space-y-6">
            {page.faqs.map((f) => (
              <div key={f.q}>
                <dt className="flex items-start gap-2 font-bold text-portal-text">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-success" />
                  {f.q}
                </dt>
                <dd className="mt-2 pl-6 leading-relaxed text-portal-text-soft">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="mt-14 rounded-[22px] bg-brand-yellow p-8 text-center">
          <h2 className="font-display text-2xl font-bold">
            Free forever for up to 40 children
          </h2>
          <p className="mx-auto mt-3 max-w-xl leading-relaxed text-portal-text-soft">
            As many classes or tutoring groups as you like, no credit card. A
            whole school is from GBP 300 a year with a 60-day free trial, and
            Litlings is free for NGOs and rural schools.
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="btn-primary mt-6 px-7 py-3 text-base"
          >
            Get started free <ArrowRight className="h-4 w-4" />
          </button>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
